// client/src/components/common/layout/PageHeader.js
import { useNavigate, useLocation } from 'react-router-dom'
import { useState } from 'react'
import useAuth from '../../../hooks/useAuth'
import useNotificationBadge from '../../../hooks/useNotificationBadge'

import './PageHeader.css'
import FeedbackModal from '../../feedback/FeedbackModal'
import HeaderButton from './HeaderButton'
import NotificationBadge from '../NotificationBadge'

function PageHeader({
  showHome = true,
  showLogout = true,
  showBack = true,
  showProfile = true,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  // No local userType fetch; derive from auth context
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const { isAdmin, logout, isLoggedIn } = useAuth()

  // Fetch unread notification count (only if logged in)
  const { count: notificationCount, loading: notificationLoading } =
    useNotificationBadge(isLoggedIn ? 60000 : null)

  // Extract leagueId from league-related routes
  const getLeagueInfo = () => {
    const path = location.pathname
    const leagueMatch = path.match(/^\/leagues\/(\d+)(?:\/|$)/)

    if (leagueMatch) {
      const leagueId = leagueMatch[1]
      const isLeagueLanding =
        path === `/leagues/${leagueId}` || path === `/leagues/${leagueId}/`
      return { leagueId, isLeagueLanding, isLeaguePage: true }
    }

    return { leagueId: null, isLeagueLanding: false, isLeaguePage: false }
  }

  const { leagueId, isLeagueLanding, isLeaguePage } = getLeagueInfo()

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="app-header-bar d-flex justify-content-between align-items-center px-2">
      {/* Left side - Feedback + Navigation */}
      <div className="d-flex flex-row flex-nowrap gap-2 gap-sm-3">
        <HeaderButton
          onClick={() => setShowFeedbackModal(true)}
          title="Feedback"
          icon="💬"
          label="Feedback"
          dataTrack="navigation.feedback"
        />

        {showBack && (
          <HeaderButton
            onClick={() => navigate(-1)}
            title="Back"
            icon="←"
            label="Back"
            dataTrack="navigation.back"
          />
        )}

        {showHome && (
          <>
            <HeaderButton
              onClick={() => navigate('/')}
              title="Home"
              icon="🏠"
              label="Home"
              dataTrack="navigation.home"
            />

            {/* League Home button - only show on league sub-pages, not on league landing */}
            {isLeaguePage && !isLeagueLanding && leagueId && (
              <HeaderButton
                onClick={() => navigate(`/leagues/${leagueId}`)}
                title="League Home"
                icon="🏆"
                label="League"
                className="modern-btn modern-btn-primary"
                dataTrack="navigation.league_home"
              />
            )}

            {isAdmin && (
              <HeaderButton
                onClick={() => navigate('/admin/dashboard')}
                title="Admin"
                icon="⚙"
                label="Admin"
                className="modern-btn modern-btn-warning"
                dataTrack="navigation.admin"
              />
            )}
          </>
        )}
      </div>

      {/* Right side - Dev indicator + User account controls */}
      <div className="d-flex flex-row flex-nowrap gap-2 gap-sm-3 align-items-center">
        {/* Notification Bell with Badge */}
        {isLoggedIn && (
          <HeaderButton
            onClick={() => navigate('/notifications')}
            title={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
            icon="🔔"
            label="Notifications"
            ariaLabel={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
            dataTrack="navigation.notifications"
            badge={
              <NotificationBadge
                count={notificationCount}
                loading={notificationLoading}
              />
            }
          />
        )}

        {showProfile && (
          <HeaderButton
            onClick={() => navigate('/profile')}
            title="Profile"
            icon="👤"
            label="Profile"
            dataTrack="navigation.profile"
          />
        )}

        {showLogout && (
          <HeaderButton
            onClick={handleLogout}
            title="Logout"
            icon="⎋"
            label="Logout"
            className="modern-btn modern-btn-danger"
            dataTrack="navigation.logout"
          />
        )}
      </div>

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          originPage={window.location.href}
        />
      )}
    </div>
  )
}

export default PageHeader
